// Currency formatting utilities
export const formatCurrency = (amount, currencyCode = 'IDR', locale = 'id-ID') => {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) {
    return '-';
  }

  // Currency symbol mapping
  const currencySymbols = {
    IDR: 'Rp',
    USD: '$',
    SGD: 'S$',
    MYR: 'RM',
    PHP: '₱',
    THB: '฿',
    VND: '₫',
  };

  const symbol = currencySymbols[currencyCode] || currencyCode;

  try {
    // Use Intl.NumberFormat for proper formatting
    const formatter = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    });

    return `${symbol} ${formatter.format(numericAmount)}`;
  } catch (error) {
    // Fallback formatting
    return `${symbol} ${numericAmount.toLocaleString()}`;
  }
};

// Percentage formatting
export const formatPercentage = (rate, decimals = 2) => {
  if (rate === null || rate === undefined || rate === '') {
    return '-';
  }

  const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
  
  if (isNaN(numericRate)) {
    return '-';
  }

  return `${numericRate.toFixed(decimals)}%`;
};

// Date formatting utilities
export const formatDate = (date, format = 'short') => {
  if (!date) return '-';

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '-';
  }

  const options = {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    datetime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    time: {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
  };

  try {
    return dateObj.toLocaleDateString('en-US', options[format] || options.short);
  } catch (error) {
    return dateObj.toLocaleDateString();
  }
};

// Relative time formatting (e.g., "2 hours ago")
export const formatRelativeTime = (date) => {
  if (!date) return '-';

  const dateObj = new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
};

// Number formatting
export const formatNumber = (number, decimals = 0) => {
  if (number === null || number === undefined || number === '') {
    return '-';
  }

  const numericValue = typeof number === 'string' ? parseFloat(number) : number;
  
  if (isNaN(numericValue)) {
    return '-';
  }

  return numericValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// File size formatting
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  if (!bytes) return '-';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Status formatting
export const formatStatus = (status) => {
  if (!status) return '-';

  const statusMap = {
    DRAFT: 'Draft',
    PENDING: 'Pending Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
  };

  return statusMap[status] || status;
};

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

// Format validation errors
export const formatValidationErrors = (errors) => {
  if (!errors || !Array.isArray(errors)) {
    return [];
  }

  return errors.map(error => {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error.message) {
      return error.field ? `${error.field}: ${error.message}` : error.message;
    }
    
    return 'Unknown validation error';
  });
};

// Format API error messages
export const formatApiError = (error) => {
  if (!error) return 'An unknown error occurred';

  // Handle different error structures
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  if (error.message) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
};

// Format business rules text
export const formatBusinessRule = (rule) => {
  if (!rule) return '-';

  const parts = [];

  if (rule.flatFee) {
    parts.push(`Flat: ${formatCurrency(rule.flatFee, rule.currency?.code)}`);
  }

  if (rule.percentageRate) {
    parts.push(`Rate: ${formatPercentage(rule.percentageRate)}`);
  }

  if (rule.minimumFee) {
    parts.push(`Min: ${formatCurrency(rule.minimumFee, rule.currency?.code)}`);
  }

  if (rule.maximumFee) {
    parts.push(`Max: ${formatCurrency(rule.maximumFee, rule.currency?.code)}`);
  }

  return parts.length > 0 ? parts.join(', ') : '-';
};

// Format entity display name
export const formatEntityName = (entity) => {
  if (!entity) return '-';
  
  if (entity.country) {
    return `${entity.name} (${entity.country})`;
  }
  
  return entity.name;
};

// Format user display name
export const formatUserName = (user) => {
  if (!user) return '-';
  
  if (user.name) {
    return user.name;
  }
  
  if (user.email) {
    return user.email.split('@')[0];
  }
  
  return 'Unknown User';
};

// Parse and format JSON data
export const formatJsonData = (data, indent = 2) => {
  if (!data) return '';
  
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return JSON.stringify(parsed, null, indent);
  } catch (error) {
    return typeof data === 'string' ? data : JSON.stringify(data);
  }
};

// Format search highlight
export const highlightSearchTerm = (text, searchTerm) => {
  if (!text || !searchTerm) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

// Format table column width
export const getColumnWidth = (content, minWidth = 100, maxWidth = 300) => {
  if (!content) return minWidth;
  
  const length = content.toString().length;
  const calculatedWidth = Math.max(length * 8, minWidth);
  
  return Math.min(calculatedWidth, maxWidth);
};
